import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';

import { MatStepperModule } from '@angular/material/stepper';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { AgentsService } from 'src/app/core/services/agents.service';
import { Subject, debounceTime, take, takeUntil } from 'rxjs';
import { ServiceFonctionnelPoste as ServiceFonctionnel } from '@models/domains/posts';

@Component({
    selector: 'app-destinataires-form',
    imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatStepperModule,
    MatAutocompleteModule
],
    templateUrl: './destinataires-form.component.html',
    styleUrls: ['./destinataires-form.component.scss']
})
export class DestinatairesFormComponent implements OnInit, OnDestroy {
  @Input() destinataireDemandeform!: FormGroup;
  @Output() formSubmit = new EventEmitter<FormGroup>();

  private _agentService: ServiceFonctionnel | undefined;
  private _direction: any;
  private readonly destroy$ = new Subject<void>();
  private readonly structureChange$ = new Subject<void>();
  private lastFormDisabledState = false;

  @Input()
  set agentService(value: ServiceFonctionnel | undefined) {
    const previousId = this._agentService?.id ?? null;
    this._agentService = value;
    const nextId = this._agentService?.id ?? null;
    if (previousId !== nextId) {
      this.triggerStructureReload();
    }
  }
  get agentService(): ServiceFonctionnel | undefined {
    return this._agentService;
  }

  @Input()
  set direction(value: any) {
    const previousId = this._direction?.id ?? null;
    this._direction = value;
    const nextId = this._direction?.id ?? null;
    if (previousId !== nextId) {
      this.triggerStructureReload();
    }
  }
  get direction(): any {
    return this._direction;
  }

  filteredOptionsDirecteur: any = [];
  filteredOptionsGpeec: any = [];
  filteredOptionsDrh: any = [];
  filteredOptionsDg: any = [];
  filteredOptionsResponsable: any = [];
  filteredOptionsDgs: any = [];
  filteredOptionsArchivageRh: any = [];

  val_ft_destinataireValidation: any = null;
  val_ft_destinataireGpeec: any = null;
  val_ft_destinataireDrh: any = null;
  val_ft_destinataireDg: any = null;
  val_ft_destinataireResponsable: any = null;
  val_ft_destinataireDgs: any = null;
  val_ft_destinataireArchivageRh: any = null;

  showDestinataireDirecteurField: boolean = true;
  showDestinataireGpeecField: boolean = true;
  showDestinataireDrhField: boolean = true;
  showDestinataireDgField: boolean = true;
  showDestinataireResponsableField: boolean = true;
  showDestinataireDgsField: boolean = true;
  showDestinataireArchivageRhField: boolean = true;

  libelleGpeecListe = 'A POSITIONNER';

  constructor(
    private readonly fb: FormBuilder,
    private readonly agentsService: AgentsService,
    private readonly cdRef: ChangeDetectorRef
  ) {}

  // Sets up subscriptions to keep validator lists aligned with form changes.
  ngOnInit() {
    this.structureChange$
      .pipe(debounceTime(0), takeUntil(this.destroy$))
      .subscribe(() => this.reloadStructureDependentDestinataires());

    this.lastFormDisabledState = this.isReadOnlyMode();
    if (!this.lastFormDisabledState) {
      this.initializeDestinatairesData();
    } else {
      this.populateReadOnlyOptions();
    }

    this.destinataireDemandeform.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      const currentlyDisabled = this.isReadOnlyMode();
      if (this.lastFormDisabledState && !currentlyDisabled) {
        this.initializeDestinatairesData();
      } else if (!this.lastFormDisabledState && currentlyDisabled) {
        this.populateReadOnlyOptions();
      }
      this.lastFormDisabledState = currentlyDisabled;
    });
  }

  // Disposes subscriptions when the component is destroyed.
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Notifies the debounce pipeline that structure data changed.
  private triggerStructureReload(): void {
    this.structureChange$.next();
  }

  // Guard against unnecessary HTTP calls when the form is shown in consultation mode.
  private reloadStructureDependentDestinataires(): void {
    if (this.isReadOnlyMode()) {
      return;
    }

    const directionId = this.direction?.id ?? null;
    const serviceId = this.agentService?.id ?? null;

    if (directionId != null) {
      this.loadDestinataireDirecteur();
      this.loadDestinataireDg();
      this.loadDestinataireArchivageRh();
    } else {
      this.hideDestinataireDirecteurField();
      this.hideDestinataireDgField();
      this.hideDestinataireArchivageRhField();
    }

    if (serviceId != null && directionId != null) {
      this.loadDestinataireResponsable();
    } else {
      this.hideDestinataireResponsableField();
    }

    if (directionId != null && serviceId != null) {
      this.loadDestinataireGpeec();
    } else {
      this.hideDestinataireGpeecField();
    }
  }

  // Emits the form group so the parent stepper can validate submission.
  onSubmit() {
    this.formSubmit.emit();
  }

  // Resets the validator selections while preserving the form structure.
  onReset() {
    this.destinataireDemandeform.reset();
  }

  // Loads Directeur validators for the selected direction while editing.
  loadDestinataireDirecteur() {
    if (this.isReadOnlyMode()) {
      return;
    }

    const directionId = this.direction?.id ?? null;
    if (!directionId) {
      this.hideDestinataireDirecteurField();
      return;
    }

    const control = this.destinataireDemandeform.get('destinataireValidationDirecteur');
    control?.reset(null, { emitEvent: false });
    control?.setValidators(Validators.required);
    control?.enable({ emitEvent: false });

    this.agentsService
      .getDestinataireDirecteur('Renouvellement', directionId)
      .pipe(take(1))
      .subscribe({
        next: (data) => {
          const valideurs = data.valideurs || [];

          if (!valideurs.length) {
            this.hideDestinataireDirecteurField();
            return;
          }
          this.showDestinataireDirecteurField = true;
          this.filteredOptionsDirecteur = valideurs;
          const defaultDirecteur = this.filteredOptionsDirecteur[0] ?? null;
          if (defaultDirecteur) {
            this.val_ft_destinataireValidation = defaultDirecteur;
            control?.setValue(defaultDirecteur, { emitEvent: false });
          }
          control?.updateValueAndValidity({ emitEvent: false });
        },
        error: (error) => console.error('Erreur lors de la récupération Directeur :', error)
      });
  }

  // Loads DRH validators once while the form remains editable.
  loadDestinataireDrh() {
    this.agentsService
      .getDestinataireDRH('Renouvellement')
      .pipe(take(1))
      .subscribe({
        next: (data) => {
          let valideurs = data.valideurs;
          let typeValideurs = data.type;

          if (!valideurs.length) {
            this.hideDestinataireDrhField();
            return;
          }
          this.filteredOptionsDrh = valideurs;
          const defaultDrh = this.filteredOptionsDrh[0];
          if (defaultDrh) {
            this.val_ft_destinataireDrh = defaultDrh;
            this.destinataireDemandeform.get('destinataireDrh')?.setValue(defaultDrh);
          }
        },
        error: (error) => console.error('Erreur lors de la récupération DRH :', error)
      });
  }

  // Loads DG validators tied to the selected direction when editable.
  loadDestinataireDg() {
    if (this.isReadOnlyMode()) {
      return;
    }

    const directionId = this.direction?.id ?? null;
    if (!directionId) {
      this.hideDestinataireDgField();
      return;
    }

    const control = this.destinataireDemandeform.get('destinataireDg');
    control?.reset(null, { emitEvent: false });
    control?.setValidators(Validators.required);
    control?.enable({ emitEvent: false });

    this.agentsService
      .getDestinataireDG('Renouvellement', directionId)
      .pipe(take(1))
      .subscribe({
        next: (data) => {
          const valideurs = data.valideurs || [];

          if (!valideurs.length) {
            this.hideDestinataireDgField();
            return;
          }
          this.showDestinataireDgField = true;
          this.filteredOptionsDg = valideurs;
          const defaultDg = this.filteredOptionsDg[0] ?? null;
          if (defaultDg) {
            this.val_ft_destinataireDg = defaultDg;
            control?.setValue(defaultDg, { emitEvent: false });
          }
          control?.updateValueAndValidity({ emitEvent: false });
        },
        error: (error) => console.error('Erreur lors de la récupération DG :', error)
      });
  }

  // Loads GPEEC validators for the selected direction/service when editable.
  loadDestinataireGpeec() {
    if (this.isReadOnlyMode()) {
      return;
    }

    const directionId = this.direction?.id ?? null;
    const serviceId = this.agentService?.id ?? null;

    if (!directionId || !serviceId) {
      this.hideDestinataireGpeecField();
      return;
    }

    const control = this.destinataireDemandeform.get('destinataireChargeGpeec');
    control?.reset(null, { emitEvent: false });
    control?.setValidators(Validators.required);
    control?.enable({ emitEvent: false });

    this.agentsService
      .getChargedGPEEC('Renouvellement', directionId, serviceId)
      .pipe(take(1))
      .subscribe({
        next: (data) => {
          const valideurs = data.valideurs || [];
          const typeValideurs = data.type;

          if (typeValideurs == 'GESTIONNAIRE_STATUTAIRE') {
            this.libelleGpeecListe = 'Gestionnaire Statutaire';
          } else if (typeValideurs == 'CHARGES_GESTION_RH') {
            this.libelleGpeecListe = 'Chargé de gestion RH';
          } else if (typeValideurs == 'CHARGES_GESTION_GPEEC') {
            this.libelleGpeecListe = 'Chargé de gestion GPEEC';
          } else {
            this.libelleGpeecListe = 'Non positionné';
          }

          if (!valideurs.length) {
            this.hideDestinataireGpeecField();
            return;
          }
          this.showDestinataireGpeecField = true;
          this.filteredOptionsGpeec = valideurs;
          const defaultGpeec = this.filteredOptionsGpeec[0] ?? null;
          if (defaultGpeec) {
            this.val_ft_destinataireGpeec = defaultGpeec;
            control?.setValue(defaultGpeec, { emitEvent: false });
          }
          control?.updateValueAndValidity({ emitEvent: false });
        },
        error: (error) => console.error('Erreur lors de la récupération GPEEC:', error)
      });
  }

  // Loads Responsable validators using the selected service and direction when editable.
  loadDestinataireResponsable() {
    if (this.isReadOnlyMode()) {
      return;
    }

    const serviceId = this.agentService?.id ?? null;
    const directionId = this.direction?.id ?? null;
    if (serviceId == null || directionId == null) {
      this.hideDestinataireResponsableField();
      return;
    }

    const control = this.destinataireDemandeform.get('destinataireResponsable');
    control?.reset(null, { emitEvent: false });
    control?.setValidators(Validators.required);
    control?.enable({ emitEvent: false });

    this.agentsService
      .getDestinataireResponsable('Renouvellement', serviceId, directionId)
      .pipe(take(1))
      .subscribe({
        next: (data) => {
          const valideurs = data.valideurs || [];

          if (!valideurs.length) {
            this.hideDestinataireResponsableField();
            return;
          }
          this.enableDestinataireResponsableField();
          this.filteredOptionsResponsable = valideurs;
          const defaultResponsable = this.filteredOptionsResponsable[0] ?? null;
          if (defaultResponsable) {
            this.val_ft_destinataireResponsable = defaultResponsable;
            control?.setValue(defaultResponsable, { emitEvent: false });
          }
          control?.updateValueAndValidity({ emitEvent: false });
        },
        error: (error) => console.error('Erreur lors de la récupération Responsable :', error)
      });
  }

  // Preloads validator lists required for interactive use.
  private initializeDestinatairesData(): void {
    this.loadDestinataireDrh();
    this.loadDestinataireDgs();
    this.triggerStructureReload();
  }

  // Rebuilds select lists from saved values when displaying the form read-only.
  private populateReadOnlyOptions(): void {
    const raw = this.destinataireDemandeform?.getRawValue?.() ?? {};

    const responsable = raw.destinataireResponsable ?? null;
    this.filteredOptionsResponsable = this.toArray(responsable);
    this.val_ft_destinataireResponsable = responsable;
    this.showDestinataireResponsableField = !!responsable;

    const directeur = raw.destinataireValidationDirecteur ?? null;
    this.filteredOptionsDirecteur = this.toArray(directeur);
    this.val_ft_destinataireValidation = directeur;
    this.showDestinataireDirecteurField = !!directeur;

    const gpeec = raw.destinataireChargeGpeec ?? null;
    this.filteredOptionsGpeec = this.toArray(gpeec);
    this.val_ft_destinataireGpeec = gpeec;
    this.showDestinataireGpeecField = !!gpeec;

    const drh = raw.destinataireDrh ?? null;
    this.filteredOptionsDrh = this.toArray(drh);
    this.val_ft_destinataireDrh = drh;
    this.showDestinataireDrhField = !!drh;

    const dg = raw.destinataireDg ?? null;
    this.filteredOptionsDg = this.toArray(dg);
    this.val_ft_destinataireDg = dg;
    this.showDestinataireDgField = !!dg;

    const dgs = raw.destinataireDgs ?? null;
    this.filteredOptionsDgs = this.toArray(dgs);
    this.val_ft_destinataireDgs = dgs;
    this.showDestinataireDgsField = !!dgs;

    const archivage = raw.destinataireArchivageRh ?? null;
    this.filteredOptionsArchivageRh = this.toArray(archivage);
    this.val_ft_destinataireArchivageRh = archivage;
    this.showDestinataireArchivageRhField = !!archivage;
  }

  // Normalises a single persisted value to an array for mat-select bindings.
  private toArray<T>(value: T | null | undefined): T[] {
    return value ? [value] : [];
  }

  // Disabled status is the signal that the container opened the form in consultation mode.
  private isReadOnlyMode(): boolean {
    return this.destinataireDemandeform?.disabled ?? false;
  }

  // Fetches the DGS validator list required for the final approval step.
  loadDestinataireDgs() {
    this.agentsService
      .getDestinataireDGS('Renouvellement')
      .pipe(take(1))
      .subscribe({
        next: (data) => {
          let valideurs = data.valideurs;
          let typeValideurs = data.type;
          if (!valideurs.length) {
            this.hideDestinataireDgsField();
            return;
          }
          this.filteredOptionsDgs = valideurs;
          const defaultDgs = this.filteredOptionsDgs[0];
          if (defaultDgs) {
            this.val_ft_destinataireDgs = defaultDgs;
            this.destinataireDemandeform.get('destinataireDgs')?.setValue(defaultDgs);
          }
        },
        error: (error) => console.error('Erreur lors de la récupération DGS :', error)
      });
  }

  // Loads the Archivage RH validator tied to the selected direction while editing.
  loadDestinataireArchivageRh() {
    if (this.isReadOnlyMode()) {
      return;
    }

    const directionId = this.direction?.id ?? null;
    const serviceId = this.agentService?.id ?? null;
    if (directionId == null) {
      this.hideDestinataireArchivageRhField();
      return;
    }

    const control = this.destinataireDemandeform.get('destinataireArchivageRh');
    control?.reset(null, { emitEvent: false });
    control?.setValidators(Validators.required);
    control?.enable({ emitEvent: false });

    this.agentsService
      .getDestinataireArchivageRh('Renouvellement', directionId, serviceId)
      .pipe(take(1))
      .subscribe({
        next: (data) => {
          let valideurs = data.valideurs;
          let typeValideurs = data.type;
          if (!valideurs.length) {
            this.hideDestinataireArchivageRhField();
            return;
          }
          this.showDestinataireArchivageRhField = true;
          this.filteredOptionsArchivageRh = valideurs;
          const defaultArchivageRh = this.filteredOptionsArchivageRh[0];
          if (defaultArchivageRh) {
            this.val_ft_destinataireArchivageRh = defaultArchivageRh;
            control?.setValue(defaultArchivageRh, { emitEvent: false });
          }
          control?.updateValueAndValidity({ emitEvent: false });
        },
        error: (error) => console.error('Erreur lors de la récupération Archivage RH :', error)
      });
  }

  // Disables and clears the DG field when the API returns nothing.
  hideDestinataireDgField() {
    const destinataireDgControl = this.destinataireDemandeform.get('destinataireDg');
    if (destinataireDgControl) {
      destinataireDgControl.reset(null, { emitEvent: false });
      destinataireDgControl.setValidators(null);
      destinataireDgControl.disable({ emitEvent: false });
      destinataireDgControl.updateValueAndValidity({ emitEvent: false });
    }

    this.showDestinataireDgField = false;
  }

  // Disables and clears the GPEEC field when the lookup fails.
  hideDestinataireGpeecField() {
    const destinataireGpeecControl = this.destinataireDemandeform.get('destinataireChargeGpeec');
    if (destinataireGpeecControl) {
      destinataireGpeecControl.reset(null, { emitEvent: false });
      destinataireGpeecControl.setValidators(null);
      destinataireGpeecControl.disable({ emitEvent: false });
      destinataireGpeecControl.updateValueAndValidity({ emitEvent: false });
    }

    this.showDestinataireGpeecField = false;
  }

  // Disables and clears the DRH field when no validator is returned.
  hideDestinataireDrhField() {
    const destinataireDrhControl = this.destinataireDemandeform.get('destinataireDrh');
    if (destinataireDrhControl) {
      destinataireDrhControl.reset(null, { emitEvent: false });
      destinataireDrhControl.setValidators(null);
      destinataireDrhControl.disable({ emitEvent: false });
      destinataireDrhControl.updateValueAndValidity({ emitEvent: false });
    }

    this.showDestinataireDrhField = false;
  }

  // Disables and clears the Directeur field when the list is empty.
  hideDestinataireDirecteurField() {
    const destinataireValidationControl = this.destinataireDemandeform.get('destinataireValidationDirecteur');
    if (destinataireValidationControl) {
      destinataireValidationControl.reset(null, { emitEvent: false });
      destinataireValidationControl.setValidators(null);
      destinataireValidationControl.disable({ emitEvent: false });
      destinataireValidationControl.updateValueAndValidity({ emitEvent: false });
    }
    this.showDestinataireDirecteurField = false;
  }

  // Disables and clears the Responsable field on empty results.
  hideDestinataireResponsableField() {
    const destinataireResponsableControl = this.destinataireDemandeform.get('destinataireResponsable');
    if (destinataireResponsableControl) {
      destinataireResponsableControl.reset(null, { emitEvent: false });
      destinataireResponsableControl.setValidators(null);
      destinataireResponsableControl.disable({ emitEvent: false });
      destinataireResponsableControl.updateValueAndValidity({ emitEvent: false });
    }

    this.showDestinataireResponsableField = false;
  }

  // Re-enables the Responsable select and reattaches validation.
  enableDestinataireResponsableField() {
    const destinataireResponsableControl = this.destinataireDemandeform.get('destinataireResponsable');
    if (destinataireResponsableControl) {
      destinataireResponsableControl.setValidators(Validators.required);
      destinataireResponsableControl.enable();
    }

    this.showDestinataireResponsableField = true;
  }

  // Disables and clears the DGS field when no validator exists.
  hideDestinataireDgsField() {
    const destinataireDgsControl = this.destinataireDemandeform.get('destinataireDgs');
    if (destinataireDgsControl) {
      destinataireDgsControl.reset(null, { emitEvent: false });
      destinataireDgsControl.setValidators(null);
      destinataireDgsControl.disable({ emitEvent: false });
      destinataireDgsControl.updateValueAndValidity({ emitEvent: false });
    }

    this.showDestinataireDgsField = false;
  }

  // Disables and clears the Archivage RH field when unavailable.
  hideDestinataireArchivageRhField() {
    const destinataireArchivageRhControl = this.destinataireDemandeform.get('destinataireArchivageRh');
    if (destinataireArchivageRhControl) {
      destinataireArchivageRhControl.reset(null, { emitEvent: false });
      destinataireArchivageRhControl.setValidators(null);
      destinataireArchivageRhControl.disable({ emitEvent: false });
      destinataireArchivageRhControl.updateValueAndValidity({ emitEvent: false });
    }

    this.showDestinataireArchivageRhField = false;
  }
}
